import { Component } from '@theme/component';

/**
 * "Find Your Routine" skin quiz.
 *
 * Scoring: each answer adds its question's `weight` (a per-question setting
 * configured in the theme editor) to the score of the collection it is
 * linked to. After the last question, the collection with the highest total
 * score is shown as the result. If two or more collections tie, the
 * collection chosen in question 1 wins the tie — question 1 is intended to
 * always carry the highest weight ("main skin concern"), so it also acts as
 * the natural tiebreaker.
 *
 * All question/option/result content — including the product cards shown on
 * the result screen (name, image, price, URL) — is rendered server-side in
 * Liquid (this component's section) and read live from Shopify. This file
 * only handles answering flow, scoring, and swapping the fully-rendered
 * result markup into view; it never stores or duplicates product data.
 */
class SkinQuiz extends Component {
  requiredRefs = [
    'questionsWrapper',
    'questions',
    'progress',
    'result',
    'resultContent',
    'resultsData',
  ];

  /** @type {Record<string, number>} */
  #scores = {};

  /** @type {string | null} */
  #firstAnswerCollection = null;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {Map<string, HTMLElement>} */
  #results = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#parseResults();
  }

  /**
   * Reads the per-collection result markup out of the <template> the
   * section renders, keyed by collection handle. Each node already contains
   * the fully-rendered result (title, description, product cards, CTA)
   * produced in Liquid, so this just keeps a detached clone ready to drop
   * into view — no product data is parsed or re-built in JS.
   */
  #parseResults() {
    const template = this.refs.resultsData;
    if (!(template instanceof HTMLTemplateElement)) return;

    this.#results.clear();

    for (const node of template.content.querySelectorAll('[data-collection]')) {
      if (!(node instanceof HTMLElement)) continue;
      const handle = node.dataset.collection;
      if (!handle) continue;

      this.#results.set(handle, /** @type {HTMLElement} */ (node.cloneNode(true)));
    }
  }

  /**
   * Handles a click on one answer option (wired via `on:click="/selectOption"`
   * in the section markup).
   * @param {Event} event
   */
  selectOption(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement) || !button.classList.contains('skin-quiz__option')) return;

    const question = button.closest('.skin-quiz__question');
    if (!(question instanceof HTMLElement)) return;

    const collection = button.dataset.collection;
    if (!collection) return;

    const weight = Number(question.dataset.weight ?? 1);

    for (const sibling of question.querySelectorAll('.skin-quiz__option')) {
      sibling.setAttribute('aria-pressed', String(sibling === button));
    }

    this.#scores[collection] = (this.#scores[collection] ?? 0) + weight;

    const questionIndex = Number(question.dataset.questionIndex ?? 0);
    if (questionIndex === 0) {
      this.#firstAnswerCollection = collection;
    }

    // Small delay so the visitor sees their answer highlighted before the
    // quiz advances to the next question (or the result).
    window.setTimeout(() => this.#advance(), 150);
  }

  #advance() {
    const questions = this.#questionList();
    const isLastQuestion = this.#currentIndex >= questions.length - 1;

    if (isLastQuestion) {
      this.#showResult();
      return;
    }

    questions[this.#currentIndex]?.setAttribute('hidden', '');
    this.#currentIndex += 1;
    questions[this.#currentIndex]?.removeAttribute('hidden');
    this.#updateProgress();
  }

  /** @returns {HTMLElement[]} */
  #questionList() {
    const { questions } = this.refs;
    if (Array.isArray(questions)) return /** @type {HTMLElement[]} */ (questions);
    return questions ? [/** @type {HTMLElement} */ (questions)] : [];
  }

  #updateProgress() {
    const dots = this.refs.progress?.querySelectorAll('.skin-quiz__progress-dot');
    dots?.forEach((dot, index) => {
      dot.setAttribute('data-active', String(index === this.#currentIndex));
    });
  }

  #showResult() {
    const winner = this.#pickWinner();
    const node = winner ? this.#results.get(winner) : undefined;
    const resultContent = this.refs.resultContent;

    if (!node || !(resultContent instanceof HTMLElement)) {
      // No result block configured for the winning collection. Fail quietly
      // rather than showing a broken/empty result card.
      console.warn('[skin-quiz] No result content configured for collection:', winner);
      return;
    }

    resultContent.replaceChildren(...node.childNodes);

    this.refs.result?.removeAttribute('hidden');
    this.refs.questionsWrapper?.setAttribute('hidden', '');
    this.refs.progress?.setAttribute('hidden', '');
  }

  /**
   * Picks the collection with the highest score. Ties are broken in favor
   * of whichever collection question 1 pointed to.
   * @returns {string | null}
   */
  #pickWinner() {
    const entries = Object.entries(this.#scores);
    if (!entries.length) return null;

    const maxScore = Math.max(...entries.map(([, score]) => score));
    const tied = entries.filter(([, score]) => score === maxScore).map(([collection]) => collection);

    if (tied.length === 1) return tied[0] ?? null;
    if (this.#firstAnswerCollection && tied.includes(this.#firstAnswerCollection)) {
      return this.#firstAnswerCollection;
    }

    return tied[0] ?? null;
  }

  /**
   * Resets the quiz back to question 1 (wired via `on:click="/reset"` on the
   * "Retake quiz" button).
   */
  reset() {
    const questions = this.#questionList();

    this.#scores = {};
    this.#firstAnswerCollection = null;
    this.#currentIndex = 0;

    questions.forEach((question, index) => {
      question.toggleAttribute('hidden', index !== 0);
      for (const option of question.querySelectorAll('.skin-quiz__option')) {
        option.setAttribute('aria-pressed', 'false');
      }
    });

    this.refs.progress?.removeAttribute('hidden');
    this.#updateProgress();
    this.refs.result?.setAttribute('hidden', '');
    this.refs.questionsWrapper?.removeAttribute('hidden');
  }
}

if (!customElements.get('skin-quiz-app')) {
  customElements.define('skin-quiz-app', SkinQuiz);
}
